/**
 * The desktop app, from the page's side.
 *
 * The same bundle ships to a browser and to the Tauri shell, so everything here answers "no" in a
 * browser rather than throwing, and every call is loaded on demand — a browser never downloads the
 * Tauri client at all.
 *
 * What the shell offers, and why each is here rather than in the page:
 *
 * - **the vault** — the agent key in the operating system's credential store. In a browser the key
 *   has to be pasted into an environment variable or a dotfile by hand, which is the worst place
 *   for it and the most common thing people get wrong.
 * - **open-url** — `devpulse://…` handed to the running window, so a link elsewhere on the machine
 *   can bring somebody to a session instead of to a login screen.
 */

/** True inside the Tauri shell. Checked without importing anything. */
export function isDesktop(): boolean {
  return typeof window !== 'undefined' && '__TAURI_INTERNALS__' in window
}

async function invoke<T>(command: string, args?: Record<string, unknown>): Promise<T> {
  const { invoke: call } = await import('@tauri-apps/api/core')
  return call<T>(command, args)
}

export interface DesktopInfo {
  version: string
  platform: string
}

/** Which build of the app this is, or null in a browser. */
export async function desktopInfo(): Promise<DesktopInfo | null> {
  if (!isDesktop()) return null
  return invoke<DesktopInfo>('desktop_info')
}

/**
 * Puts the agent key in this machine's credential store.
 *
 * The key never goes to a file, and never comes back anywhere but here.
 */
export async function vaultStore(key: string): Promise<void> {
  await invoke('vault_store', { key })
}

/**
 * The agent key never reaches the page (docs/refactor/39): calls that need it go through the app (src-tauri/src/agent_api.rs),
 * with this marker where the key goes, and the app puts the stored key in its place — only for the Vephus API.
 */
export const AGENT_KEY_MARKER = 'vault:agent-key'

/** The marker when a key is stored, null when there is none: what the runner, telemetry and inventory use as "the key". */
export async function agentKeyRef(): Promise<string | null> {
  return (await invoke<boolean>('vault_has').catch(() => false)) ? AGENT_KEY_MARKER : null
}

/** fetch() for calls that carry the agent key, made by the app. Only for the Vephus API; the answer as a Response. */
export async function agentFetch(input: string | URL | Request, init: RequestInit = {}): Promise<Response> {
  const url = typeof input === 'string' ? input : input instanceof URL ? input.toString() : input.url
  const headers: Array<[string, string]> = []
  new Headers(init.headers ?? {}).forEach((value, name) => headers.push([name, value]))
  const wait = /[?&]wait=(\d+)/.exec(url)
  const answer = await invoke<{ status: number; body: string }>('agent_fetch', {
    url,
    method: init.method ?? 'GET',
    headers,
    body: typeof init.body === 'string' ? init.body : null,
    // A long poll waits on the server: give it that long and a margin.
    timeoutSecs: wait ? Number(wait[1]) + 30 : 120,
  }).catch((error) => {
    throw new TypeError(String(error))
  })
  const empty = answer.status === 204 || answer.status === 205 || answer.status === 304
  return new Response(empty ? null : answer.body, { status: answer.status })
}

export async function vaultClear(): Promise<void> {
  if (!isDesktop()) return
  await invoke('vault_clear')
}

/** Whether a key is stored, without reading it — enough for a screen that says "connected". */
/**
 * Keeps the computer from going to sleep (`on`) while it takes work, or lets it sleep again (docs/refactor/28). The screen
 * may still turn off. Returns whether it is held awake now; false in a browser.
 */
export async function powerKeepAwake(on: boolean): Promise<boolean> {
  if (!isDesktop()) return false
  return invoke<boolean>('power_keep_awake', { on })
}

/** Whether the app opens (hidden, in the tray) when the computer starts; false in a browser. */
export async function autostartEnabled(): Promise<boolean> {
  if (!isDesktop()) return false
  return invoke<boolean>('autostart_enabled')
}

/** Opens the app when the computer starts, or stops; returns what is set now. */
export async function autostartSet(on: boolean): Promise<boolean> {
  if (!isDesktop()) return false
  return invoke<boolean>('autostart_set', { on })
}

export async function vaultHas(): Promise<boolean> {
  if (!isDesktop()) return false
  return invoke<boolean>('vault_has')
}

/**
 * Opens a web address in the default browser. In a browser, a new tab.
 * The desktop broker takes http and https only.
 */
export async function openExternal(url: string): Promise<void> {
  if (!isDesktop()) {
    window.open(url, '_blank', 'noopener')
    return
  }
  await invoke('open_external', { url })
}

/** The web app's own address, for "View on the web": set in the desktop build, the page's origin elsewhere. */
export function siteUrl(): string {
  return (import.meta.env?.VITE_SITE_URL ?? window.location.origin).replace(/\/+$/, '')
}

/** A git repository folder the person picked on this machine. */
export interface RepoFolder {
  path: string
  /** The folder's name, which is what the agent's hooks report as the repository. */
  name: string
  remote_url: string | null
  branch: string | null
}

/**
 * Asks for a repository folder with the native dialog. Null when cancelled or in a browser;
 * throws `not-a-repository` for a folder without git.
 */
export async function repoPick(): Promise<RepoFolder | null> {
  if (!isDesktop()) return null
  return invoke<RepoFolder | null>('repo_pick')
}

/** A folder picked before, as it is now; null when it is gone or was never picked on this machine. */
export async function repoDescribe(path: string): Promise<RepoFolder | null> {
  if (!isDesktop()) return null
  return invoke<RepoFolder | null>('repo_describe', { path })
}

/** The repository's `git status --porcelain`, for telling which files a run created. */
export async function repoStatus(path: string): Promise<Array<{ code: string; path: string }>> {
  if (!isDesktop()) return []
  return invoke<Array<{ code: string; path: string }>>('repo_status', { path })
}

let machineNameCache: Promise<string | null> | null = null

/** This computer's name as the system calls it ("DIOGO-PC"), asked once. Null in a browser. */
export function desktopMachineName(): Promise<string | null> {
  if (!isDesktop()) return Promise.resolve(null)
  machineNameCache ??= invoke<string | null>('machine_name').catch(() => null)
  return machineNameCache
}

/** A native notification from the desktop app (the inbox's news). Nothing in a browser. */
export async function desktopNotify(title: string, body: string): Promise<void> {
  if (!isDesktop()) return
  await invoke<void>('notify', { title, body })
}

/** One file a turn changed, against the checkpoint taken before it (desktop/src-tauri/src/checkpoints.rs). */
export interface FileChange {
  path: string
  /** "A" added, "M" modified, "D" deleted. */
  status: string
  added: number | null
  deleted: number | null
  binary: boolean
}

/** Snapshots a picked folder before a turn, without touching it; returns the checkpoint's id. */
export async function repoCheckpoint(path: string, name: string): Promise<string> {
  return invoke<string>('repo_checkpoint', { path, name })
}

/** What changed in the folder since a checkpoint. */
export async function repoChanges(path: string, checkpoint: string): Promise<FileChange[]> {
  return invoke<FileChange[]>('repo_changes', { path, checkpoint })
}

/** One file's change since a checkpoint, as a unified diff. */
export async function repoFileDiff(path: string, checkpoint: string, file: string): Promise<string> {
  return invoke<string>('repo_file_diff', { path, checkpoint, file })
}

/** Undoes one file of a turn: back to the checkpoint (gone, if the turn created it). */
export async function repoRestoreFile(path: string, checkpoint: string, file: string): Promise<void> {
  return invoke<void>('repo_restore_file', { path, checkpoint, file })
}

/** Undoes a whole turn. Returns how many files went back. */
export async function repoRestoreCheckpoint(path: string, checkpoint: string): Promise<number> {
  return invoke<number>('repo_restore_checkpoint', { path, checkpoint })
}

/** A picked repository's files as git sees them, with its branch and head. */
export async function repoFiles(path: string): Promise<{ branch: string | null; head: string | null; files: string[] }> {
  return invoke('repo_files', { path })
}

export async function repoRead(folder: string, file: string): Promise<string> {
  return invoke<string>('repo_read', { folder, file })
}

export async function repoWrite(folder: string, file: string, content: string): Promise<void> {
  await invoke('repo_write', { folder, file, content })
}

/**
 * One request to an AI provider's official API, sent by the desktop's Rust side with the key from
 * the credential store (ai.rs). The page sends only the body; the answer comes back as status + body.
 */
export async function aiRequest(provider: 'anthropic' | 'openai', body: string, beta?: string): Promise<{ status: number; body: string }> {
  if (!isDesktop()) throw new Error('The official API mode needs the desktop app.')
  return invoke<{ status: number; body: string }>('ai_request', { provider, body, beta: beta ?? null })
}

export interface RepoBranches {
  current: string | null
  local: string[]
  /** Remote branches with no local one yet, as `origin/name`. */
  remote: string[]
}

/** The branches of a picked repository; with `fetch`, asks the remote first. */
export async function repoBranches(path: string, fetch = false): Promise<RepoBranches> {
  return invoke<RepoBranches>('repo_branches', { path, fetch })
}

/** Switches to a branch (or creates it from the current one). Throws git's own message when it refuses. */
export async function repoCheckout(path: string, branch: string, create = false): Promise<string> {
  return invoke<string>('repo_checkout', { path, branch, create })
}

/**
 * Clones a repository into a folder picked with the native dialog, with the person's own git
 * credentials. Null when cancelled; throws `no-access` when git was refused, `exists` when the
 * folder already has one by that name.
 */
export async function repoClone(url: string): Promise<RepoFolder | null> {
  if (!isDesktop()) return null
  return invoke<RepoFolder | null>('repo_clone', { url })
}

/** Folders added to the workspace (any folder, git or not), through the native dialog. */
export interface WorkspaceFolder {
  path: string
  name: string
  git: boolean
}

export async function workspaceAdd(): Promise<WorkspaceFolder | null> {
  return invoke<WorkspaceFolder | null>('workspace_add')
}

export async function workspaceFolders(): Promise<WorkspaceFolder[]> {
  if (!isDesktop()) return []
  return invoke<WorkspaceFolder[]>('workspace_folders')
}

export async function workspaceRemove(path: string): Promise<void> {
  await invoke('workspace_remove', { path })
}

export async function workspaceList(path: string): Promise<Array<{ name: string; path: string; dir: boolean; size: number }>> {
  return invoke('workspace_list', { path })
}

export async function workspaceRead(path: string): Promise<string> {
  return invoke<string>('workspace_read', { path })
}

export async function workspaceWrite(path: string, content: string): Promise<void> {
  await invoke('workspace_write', { path, content })
}

/** A project open in the sidebar (Open project…): any folder, with git's facts when it has git. */
export interface DesktopProject {
  path: string
  name: string
  git: boolean
  remote_url: string | null
  branch: string | null
}

/**
 * Open project…: the native folder dialog. A git folder becomes a picked repository folder, any
 * other a workspace folder. Null when cancelled or in a browser.
 */
export async function projectOpen(): Promise<DesktopProject | null> {
  if (!isDesktop()) return null
  return invoke<DesktopProject | null>('project_open')
}

/**
 * New project: the native dialog asks where, and the folder `name` is made there as a git
 * repository with a README, a .gitignore and a first commit; it opens as a project. Null when
 * cancelled or in a browser; throws `invalid-name` or `exists`.
 */
export async function projectCreate(name: string, summary: string): Promise<DesktopProject | null> {
  if (!isDesktop()) return null
  return invoke<DesktopProject | null>('project_create', { name, summary })
}

/** The GitHub account this computer is signed in to with `gh`; null without gh, signed out, or in a browser (docs/refactor/58). */
export async function githubAccount(): Promise<string | null> {
  if (!isDesktop()) return null
  return invoke<string | null>('github_account').catch(() => null)
}

/**
 * Creates a project just made on the signed-in GitHub account and pushes it; resolves with its address. Throws
 * `gh-missing`, `gh-signed-out`, `name-taken`, `has-remote` or `github-failed`.
 */
export async function projectPublishGithub(path: string, isPrivate: boolean): Promise<string> {
  return invoke<string>('project_publish_github', { path, private: isPrivate })
}

/** The open projects, most recent first, as the desktop remembers them across restarts. */
export async function projectsList(): Promise<DesktopProject[]> {
  if (!isDesktop()) return []
  return invoke<DesktopProject[]>('projects_list')
}

/** A folder already allowed here (picked, cloned or found) put at the top of the open projects. */
export async function projectAdd(path: string): Promise<DesktopProject | null> {
  if (!isDesktop()) return null
  return invoke<DesktopProject>('project_add', { path })
}

export interface FoundRepository {
  id: string
  folder: RepoFolder
}

/**
 * Looks for the linked repositories on this computer by their git remote, in the usual code folders
 * (docs/refactor/53). Those found become usable here at once. Empty in a browser.
 */
export async function repoFind(wanted: { id: string; name: string; remoteUrl: string | null }[]): Promise<FoundRepository[]> {
  if (!isDesktop() || wanted.length === 0) return []
  return invoke<FoundRepository[]>('repo_find', { wanted })
}

/** For each path, whether it is a file in a folder the app works in — what "Abrir" can be offered for (51, N6). */
export async function pathsOpenable(paths: string[]): Promise<boolean[]> {
  if (!isDesktop() || paths.length === 0) return paths.map(() => false)
  return invoke<boolean[]>('paths_openable', { paths })
}

/** Opens a file with the computer's own program for it; never a program or a script (`runs`). */
export async function pathOpen(path: string): Promise<void> {
  await invoke('path_open', { path })
}

/** Shows the file selected in Explorer or Finder. */
export async function pathReveal(path: string): Promise<void> {
  await invoke('path_reveal', { path })
}

/** Takes a project out of the sidebar. Nothing on disk is touched. */
export async function projectClose(path: string): Promise<void> {
  if (!isDesktop()) return
  await invoke('project_close', { path })
}

export async function repoForget(path: string): Promise<void> {
  if (!isDesktop()) return
  await invoke('repo_forget', { path })
}

/** A Claude skill installed on this machine (desktop/src-tauri/src/skills.rs). */
export interface MachineSkill {
  /** Its folder's name, which is how it is switched. */
  name: string
  description: string
  /** On: in `skills/`. Off: moved to `skills-disabled/`, where Claude Code does not look. */
  enabled: boolean
  /** "user": ~/.claude. "project": the open project's .claude. */
  scope: 'user' | 'project'
  source: string | null
}

/** The skills on this machine, and the project's when its folder is given. Empty in a browser. */
export async function skillsList(project?: string | null): Promise<MachineSkill[]> {
  if (!isDesktop()) return []
  return invoke<MachineSkill[]>('skills_list', { project: project ?? null })
}

/** Switches a skill on or off by moving its folder; nothing is deleted. */
export async function skillSetEnabled(name: string, scope: MachineSkill['scope'], project: string | null, enabled: boolean): Promise<void> {
  await invoke('skill_set_enabled', { name, scope, project, enabled })
}

/** An MCP server added on this machine (desktop/src-tauri/src/mcp.rs). Secrets never come back. */
export interface McpServer {
  name: string
  enabled: boolean
  kind: 'stdio' | 'http' | 'sse'
  command: string | null
  args: string[]
  url: string | null
  env_keys: string[]
  header_keys: string[]
}

export async function mcpList(): Promise<McpServer[]> {
  if (!isDesktop()) return []
  return invoke<McpServer[]>('mcp_list')
}

/** Adds the servers in pasted JSON (Claude Code's format); `name` names a bare server. Returns their names. */
export async function mcpAdd(json: string, name?: string | null): Promise<string[]> {
  return invoke<string[]>('mcp_add', { json, name: name ?? null })
}

export async function mcpRemove(name: string): Promise<void> {
  await invoke('mcp_remove', { name })
}

export async function mcpSetEnabled(name: string, enabled: boolean): Promise<void> {
  await invoke('mcp_set_enabled', { name, enabled })
}

/** The servers that are on, joined for an IDE, with `${VARIABLE}` where each secret goes. */
export async function mcpExport(): Promise<string> {
  return invoke<string>('mcp_export')
}

/** Starts a server for a moment and lists its tools. */
export async function mcpTest(name: string): Promise<{ ok: boolean; tools: string[]; error: string | null }> {
  return invoke('mcp_test', { name })
}

/**
 * Calls back with each `devpulse://…` the shell was handed, and returns the way to stop listening.
 *
 * A browser gets a listener that does nothing, so callers need no branch of their own.
 */
export async function onOpenUrl(handle: (urls: string[]) => void): Promise<() => void> {
  if (!isDesktop()) return () => {}

  const { listen } = await import('@tauri-apps/api/event')
  return listen<string[]>('devpulse://open-url', (event) => handle(event.payload))
}

/** A service shared at a public address by Publicar (src-tauri/src/tunnels.rs). */
export interface SharedTunnel {
  port: number
  name: string
  url: string
  /** Seconds until it closes on its own. */
  closesIn: number
}

/** Shares what answers on `port` at a public trycloudflare.com address; rejects with the reason the app refused. */
export async function tunnelOpen(port: number, name: string): Promise<SharedTunnel> {
  return invoke<SharedTunnel>('tunnel_open', { port, name })
}

/** Stops sharing one port, or everything. */
export async function tunnelClose(port?: number): Promise<void> {
  await invoke('tunnel_close', { port: port ?? null })
}

/** What this computer is sharing now. */
export async function tunnelList(): Promise<SharedTunnel[]> {
  return invoke<SharedTunnel[]>('tunnel_list')
}

/** An extra agent's own checkout of a repository (ADR-0011, src-tauri/src/worktrees.rs). */
export interface AgentWorktree {
  path: string
  branch: string
}

export async function worktreeFor(folder: string, lane: number): Promise<AgentWorktree> {
  return invoke<AgentWorktree>('worktree_for', { path: folder, lane })
}

export async function worktreeCommit(path: string, message: string): Promise<string | null> {
  return invoke<string | null>('worktree_commit', { path, message })
}

/** One turn of agent `lane` into the person's folder, uncommitted; applied false when it would not fit (docs/refactor/55). */
export async function worktreeBring(folder: string, lane: number, commit: string): Promise<{ applied: boolean; files: string[] }> {
  return invoke<{ applied: boolean; files: string[] }>('worktree_bring', { path: folder, lane, commit })
}

export async function worktreeDiscard(folder: string, lane: number): Promise<boolean> {
  return invoke<boolean>('worktree_discard', { path: folder, lane })
}

/** What the app could not report to Vephus yet (docs/refactor/52): a JSON file of this account; empty when nothing waits. */
export async function outboxRead(): Promise<string> {
  if (!isDesktop()) return ''
  return invoke<string>('outbox_read')
}

export async function outboxWrite(content: string): Promise<void> {
  if (!isDesktop()) return
  await invoke('outbox_write', { content })
}
